<script>
import DealerActionLink from './DealerActionLink.vue'
import dealerQuery from './query/dealer.gql'
import { request } from '~/api'
import ElevatedSurface from '~/components/ElevatedSurface.vue'
import SvgIcon from '~/components/SvgIcon.vue'
import phoneFilter from '~/filters/phone.js'
import location from '~/icons/dealerInfo/location.svg'
import website from '~/icons/dealerInfo/website.svg'

export default {
  name: 'DealerInfo',
  components: {
    DealerActionLink,
    ElevatedSurface,
    SvgIcon
  },
  filters: {
    urlFilter (dealerUrl) {
      if (!dealerUrl) {
        return
      }
      return dealerUrl.replace(/(?:^https?:\/\/(?:www\.)?)|(?:\/(?:index.html?)?$)/gm, '')
    },
    phoneFilter
  },
  props: {
    dealerId: {
      type: String,
      default: null
    },
    dealerPhone: {
      type: String,
      default: null
    }
  },
  data: () => ({
    icons: {
      location,
      phone: '$phone',
      website
    },
    dealer: {}
  }),
  computed: {
    phoneDisplay () {
      return this.dealerPhone || this.dealer.phone
    },
    dealerAddress () {
      if (!this.dealer.address) return ''

      return 'https://www.google.com/maps/search/?api=1&query=' + this.dealer.address.street + ', ' + this.dealer.address.locality + ', ' + this.dealer.address.region + ' ' + this.dealer.address.postalCode
    },
    actionLinks () {
      if (!this.dealer) return []

      return [
        {
          shouldShow: !!this.dealer.phone,
          href: 'tel:' + this.dealer.phone,
          icon: this.icons.phone,
          text: 'CALL'
        },
        {
          shouldShow: !!this.dealer.address,
          href: this.dealerAddress,
          icon: this.icons.location,
          text: 'DIRECTIONS',
          target: '_blank'
        },
        {
          shouldShow: !!this.dealer.url,
          href: this.dealer.url,
          icon: this.icons.website,
          text: 'WEBSITE',
          target: '_blank'
        }
      ].filter(item => item.shouldShow)
    }
  },
  watch: {
    dealerId () {
      this.getDealer()
    }
  },
  mounted () {
    this.getDealer()
  },
  serverPrefetch: vm => vm.getDealer(),
  methods: {
    async getDealer () {
      if (!this.dealerId || this.dealerId === this.dealer.id) return

      const { data: { dealer } } = await request(dealerQuery, {
        dealerId: this.dealerId
      })

      this.dealer = dealer || {}
    }
  }
}
</script>

<template>
  <ElevatedSurface
    v-if="dealer.name"
    class="surface--dealer-info"
  >
    <VFlex class="px-6 pt-4">
      <VImg
        v-if="dealer.logo"
        :src="dealer.logo.url"
        width="128"
      />
    </VFlex>
    <div
      class="surface--dealer-info__header align-center px-6 py-4"
    >
      <VFlex>
        <h3 class="text-h6 font-weight-medium surface--dealer-info__dealer-name">
          {{ dealer.name }}
        </h3>
        <div class="d-flex mt-4">
          <div
            v-for="(actionLink, index) in actionLinks"
            v-show="actionLink.providerId || actionLink.text"
            :key="index"
            class="grow"
          >
            <DealerActionLink
              v-if="actionLink.text"
              :id="`dealer-info--${actionLink.text.toLowerCase()}`"
              v-bind="actionLink"
            />
          </div>
        </div>
      </VFlex>
    </div>
    <div
      v-if="dealer.address"
      target="_blank"
      class="surface--dealer-info__header surface--dealer-info__item-wrapper align-center px-6 py-4"
    >
      <a
        :href="dealerAddress"
        class="d-flex"
      >
        <SvgIcon
          :src="icons.location"
          class="surface--dealer-info__item-body-icon"
        />
        <div class="surface--dealer-info__item-body">
          <span
            class="text-body-2 font-weight-regular ml-4"
          >
            {{ dealer.address.street }}
          </span>
          <span
            class="text-body-2 font-weight-regular ml-4"
          >
            {{ dealer.address.locality }}, {{ dealer.address.region }} {{ dealer.address.postalCode }}
          </span>
        </div>
      </a>
    </div>
    <a
      v-if="phoneDisplay"
      :href="'tel:' + phoneDisplay"
      class="surface--dealer-info__header surface--dealer-info__item-wrapper align-center px-6 py-4"
    >
      <VIcon>
        {{ icons.phone }}
      </VIcon>
      <span
        class="text-body-2 font-weight-regular primary--text ml-4"
      >
        {{ phoneDisplay | phoneFilter }}
      </span>
    </a>
  </ElevatedSurface>
</template>

<style lang="scss">
.surface--dealer-info {
  &__header {
    border-bottom: 1px solid var(--surface-shadow);
  }
  &__item-body {
    display: flex;
    flex-direction: column;
  }
  &__item-wrapper {
    display: flex;
    align-items: center;
    line-height: 20px;
    text-decoration: none;
    > a, span {
      color: var(--v-dark-lighten1);
      text-decoration: none;
    }
    > a {
      display: flex;
      align-items: center;
    }
  }
  &__item-body-icon {
    fill: var(--v-grey-darken1) !important;
    width: 24px
  }
  & .v-btn--outlined {
    text-transform: none;
  }
}
</style>
